import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';

// ─────────────────────────────────────────────────────────────────────────
// THE COST ESTIMATOR
// Window Washing Guide — Tool 05 of 09
// ─────────────────────────────────────────────────────────────────────────
//
// Estimates DIY and professional window cleaning costs for a residential
// job, with regional pricing data and accessibility multipliers.
//
// Methodology:
//   • Pro pricing baseline: per-pane average from IWCA 2024 industry survey,
//     $4–$10 standard residential, scaled by region cost-of-living index.
//   • DIY cost: labor at $0/hr (your own time) + materials amortized over
//     job count. Materials baseline: $80 starter kit, ~50 jobs lifetime.
//   • Story multipliers: 1× ground floor, 1.4× second story (ladder),
//     2.2× third+ (rope-access or pole work, recommended pro only).
//   • Accessibility multipliers: 1× standard, 1.3× hard access (over decks,
//     bushes, AC units), 1.8× very hard (interior atriums, fixed glass).
//
// Sources: IWCA 2024 Residential Pricing Report, BLS Cost-of-Living Index
// (regional), HomeAdvisor 2024 Service Pricing Database.
//
// ─────────────────────────────────────────────────────────────────────────

// Regional cost-of-living index (multiplier vs. national average)
// Pro labor scales nearly linearly with this; materials are flat nationwide.
const REGIONS = [
  { id: 'ne-major', name: 'Northeast — major metro', states: 'NYC, Boston, DC', col: 1.42, tier: 'High' },
  { id: 'ne-other', name: 'Northeast — suburban',    states: 'Upstate NY, NJ, CT', col: 1.18, tier: 'High' },
  { id: 'west-major', name: 'West Coast — major metro', states: 'SF, LA, Seattle', col: 1.55, tier: 'Highest' },
  { id: 'west-other', name: 'West Coast — secondary', states: 'Portland, San Diego', col: 1.22, tier: 'High' },
  { id: 'south-major', name: 'South — major metro', states: 'Atlanta, Miami, Dallas, Houston', col: 1.05, tier: 'Average' },
  { id: 'south-other', name: 'South — secondary', states: 'Charlotte, Nashville, Phoenix', col: 0.98, tier: 'Average' },
  { id: 'midwest-major', name: 'Midwest — major metro', states: 'Chicago, Minneapolis, Detroit', col: 1.04, tier: 'Average' },
  { id: 'midwest-other', name: 'Midwest — secondary', states: 'St. Louis, Indianapolis', col: 0.91, tier: 'Below avg' },
  { id: 'mountain', name: 'Mountain West', states: 'Denver, SLC, Las Vegas', col: 1.08, tier: 'Average' },
  { id: 'rural', name: 'Rural / small market', states: 'Anywhere outside metro', col: 0.85, tier: 'Below avg' },
];

// Story multipliers
const STORIES = [
  { id: '1', label: 'Ground floor only', desc: 'All windows reachable from ground or low step stool', mult: 1.0, risk: 'low' },
  { id: '2', label: 'Two stories', desc: 'Includes second-story windows requiring ladder or pole', mult: 1.4, risk: 'med' },
  { id: '3', label: 'Three stories or higher', desc: 'Rope access or commercial-grade pole system required', mult: 2.2, risk: 'high' },
];

// Accessibility multipliers
const ACCESS = [
  { id: 'standard', label: 'Standard access', desc: 'Open ground around perimeter, level footing', mult: 1.0 },
  { id: 'hard', label: 'Hard access', desc: 'Over decks, AC units, dense landscaping, sloped ground', mult: 1.3 },
  { id: 'very-hard', label: 'Very hard access', desc: 'Interior atriums, fixed glass, restricted angles', mult: 1.8 },
];

// Pro pricing baseline (per pane, national average, before multipliers)
const PRO_BASE_PER_PANE = 7.50;
// Minimum pro service charge (most pros have a job minimum)
const PRO_MIN_CHARGE = 150;
// Whether the inside is included usually doubles the per-pane count
// (a pane has an inside face and an outside face)

// DIY cost model
const DIY_KIT_COST = 80;
const DIY_KIT_LIFETIME_JOBS = 50;
const DIY_PER_JOB_CONSUMABLES = 4; // distilled water, refill of soap
const DIY_HOURS_PER_PANE = 0.05; // 3 minutes per pane — generous for a pro,
                                  // we then 2× this for DIY rookie pace below
const DIY_ROOKIE_MULT = 2.2;

export default function CostEstimator() {
  const [paneCount, setPaneCount] = useState(20);
  const [bothSides, setBothSides] = useState(true);
  const [regionId, setRegionId] = useState('south-major');
  const [storyId, setStoryId] = useState('2');
  const [accessId, setAccessId] = useState('standard');
  const [hourlyValue, setHourlyValue] = useState(35); // user's own time value
  const [dark, setDark] = useState(false);
  const [showAssumptions, setShowAssumptions] = useState(false);

  const region = REGIONS.find((r) => r.id === regionId);
  const story = STORIES.find((s) => s.id === storyId);
  const access = ACCESS.find((a) => a.id === accessId);

  // Effective pane count (both sides doubles)
  const effectivePanes = bothSides ? paneCount * 2 : paneCount;

  // Pro estimate
  const proEstimate = useMemo(() => {
    const base = effectivePanes * PRO_BASE_PER_PANE;
    const adjusted = base * region.col * story.mult * access.mult;
    const final = Math.max(adjusted, PRO_MIN_CHARGE);
    // Range: ±20% to reflect real-world variance
    return {
      low: Math.round(final * 0.8),
      mid: Math.round(final),
      high: Math.round(final * 1.2),
      hitMin: adjusted < PRO_MIN_CHARGE,
    };
  }, [effectivePanes, region, story, access]);

  // DIY estimate
  const diyEstimate = useMemo(() => {
    const materialsAmortized = (DIY_KIT_COST / DIY_KIT_LIFETIME_JOBS) + DIY_PER_JOB_CONSUMABLES;
    const hours = effectivePanes * DIY_HOURS_PER_PANE * DIY_ROOKIE_MULT * story.mult * access.mult;
    const timeValue = hours * hourlyValue;
    return {
      materials: Math.round(materialsAmortized),
      hours: Math.round(hours * 10) / 10,
      timeValue: Math.round(timeValue),
      totalWithTime: Math.round(materialsAmortized + timeValue),
      totalCashOnly: Math.round(materialsAmortized),
    };
  }, [effectivePanes, hourlyValue, story, access]);

  const savings = proEstimate.mid - diyEstimate.totalWithTime;
  const savingsPercent = Math.round((savings / proEstimate.mid) * 100);

  // Breakeven point: above what hourly value does pro become better deal?
  const breakevenHourly = useMemo(() => {
    const proMid = proEstimate.mid;
    const materials = (DIY_KIT_COST / DIY_KIT_LIFETIME_JOBS) + DIY_PER_JOB_CONSUMABLES;
    const hours = effectivePanes * DIY_HOURS_PER_PANE * DIY_ROOKIE_MULT * story.mult * access.mult;
    if (hours === 0) return 0;
    return Math.round((proMid - materials) / hours);
  }, [proEstimate.mid, effectivePanes, story, access]);

  // ─── theme ────────────────────────────────────────────────────────────
  const T = dark
    ? {
        bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A',
      }
    : {
        bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A',
      };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', 'Helvetica Neue', sans-serif";
  const fontMono = "'JetBrains Mono', 'Courier New', monospace";

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        color: T.ink,
        fontFamily: fontBody,
        transition: 'background 0.3s, color 0.3s',
        padding: '32px 20px 80px',
      }}
    >
      <StaticSeo schemaType="webApplication" path="/tools/cost-estimator" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter+Tight:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
      />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .reveal { animation: fadeUp 0.6s cubic-bezier(.2,.7,.3,1) both; }
        .reveal-1 { animation-delay: 0.05s; }
        .reveal-2 { animation-delay: 0.15s; }
        .reveal-3 { animation-delay: 0.25s; }
        .reveal-4 { animation-delay: 0.35s; }

        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          cursor: pointer;
        }
        input[type="range"]::-webkit-slider-track {
          background: ${T.rule};
          height: 2px;
          border-radius: 0;
        }
        input[type="range"]::-moz-range-track {
          background: ${T.rule};
          height: 2px;
          border-radius: 0;
        }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px; height: 16px;
          background: ${T.ink};
          border-radius: 50%;
          margin-top: -7px;
        }
        input[type="range"]::-moz-range-thumb {
          width: 16px; height: 16px;
          background: ${T.ink};
          border-radius: 50%;
          border: none;
        }

        .opt-tile {
          transition: background 0.2s, border-color 0.2s, color 0.2s;
        }
        .opt-tile:hover {
          background: ${T.surface};
        }
        .opt-tile.active {
          background: ${T.surface};
          border-color: ${T.sienna} !important;
        }

        .ghost-btn:hover { color: ${T.sienna}; }
        .pill-btn:hover { background: ${T.ink}; color: ${T.bg}; }

        @media (max-width: 820px) {
          .grid-2col { grid-template-columns: 1fr !important; }
          .h1-title { font-size: 38px !important; line-height: 1.05 !important; }
          .summary-num { font-size: 56px !important; }
        }
      `}</style>

      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {/* Header */}
        <header
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            paddingBottom: 20, borderBottom: `1px solid ${T.rule}`, marginBottom: 56,
          }}
        >
          <div style={{ fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em' }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </div>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: '50%',
              width: 32, height: 32,
              color: T.inkSoft, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Title */}
        <div className="reveal reveal-1">
          <div
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 20,
            }}
          >
            § THE TOOLS &nbsp;·&nbsp; 05 / 09 &nbsp;·&nbsp; REGIONAL DATA
          </div>

          <h1
            className="h1-title"
            style={{
              fontFamily: fontDisplay, fontSize: 60, fontWeight: 400,
              lineHeight: 1.04, letterSpacing: '-0.025em',
              margin: '0 0 24px', maxWidth: 800,
            }}
          >
            The Cost{' '}
            <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
              Estimator
            </em>
            .
          </h1>

          <p
            style={{
              fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
              lineHeight: 1.5, color: T.inkSoft,
              maxWidth: 680, margin: '0 0 12px',
            }}
          >
            Square footage, story, accessibility, region. DIY versus pro pricing,
            in one glance — with the assumptions visible, not buried.
          </p>

          <p
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.05em', color: T.inkFaint, marginBottom: 56,
            }}
          >
            Sources: IWCA 2024 Residential Pricing Report, BLS Cost-of-Living Index, HomeAdvisor 2024.
          </p>
        </div>

        {/* Inputs + Output side by side */}
        <div
          className="grid-2col reveal reveal-2"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 380px',
            gap: 56,
            marginBottom: 56,
          }}
        >
          {/* LEFT — inputs */}
          <div>
            {/* Pane count slider */}
            <div style={{ marginBottom: 40 }}>
              <div
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'baseline', marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.sienna,
                  }}
                >
                  ◆ HOW MANY PANES
                </div>
                <div
                  style={{
                    fontFamily: fontDisplay, fontSize: 32, fontWeight: 400,
                    letterSpacing: '-0.02em', color: T.ink,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {paneCount}
                </div>
              </div>
              <input
                type="range"
                min="1"
                max="80"
                value={paneCount}
                onChange={(e) => setPaneCount(parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
              <div
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.1em', color: T.inkFaint,
                  marginTop: 8,
                }}
              >
                <span>1</span>
                <span>SMALL HOME ≈ 12</span>
                <span>AVG HOME ≈ 24</span>
                <span>LARGE HOME ≈ 40</span>
                <span>80</span>
              </div>

              {/* Both sides toggle */}
              <label
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  marginTop: 20, cursor: 'pointer',
                  fontFamily: fontBody, fontSize: 14, color: T.ink,
                }}
              >
                <input
                  type="checkbox"
                  checked={bothSides}
                  onChange={(e) => setBothSides(e.target.checked)}
                  style={{
                    width: 18, height: 18,
                    accentColor: T.sienna, cursor: 'pointer',
                  }}
                />
                <span>
                  Clean both sides{' '}
                  <span style={{ color: T.inkFaint, fontFamily: fontMono, fontSize: 11, letterSpacing: '0.05em' }}>
                    (× 2 — most common)
                  </span>
                </span>
              </label>
            </div>

            {/* Region picker */}
            <div style={{ marginBottom: 40 }}>
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 16,
                }}
              >
                ◆ WHERE
              </div>
              <select
                value={regionId}
                onChange={(e) => setRegionId(e.target.value)}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: `1px solid ${T.ink}`,
                  fontFamily: fontDisplay,
                  fontSize: 22,
                  fontWeight: 400,
                  color: T.ink,
                  padding: '8px 0',
                  outline: 'none',
                  cursor: 'pointer',
                }}
              >
                {REGIONS.map((r) => (
                  <option key={r.id} value={r.id} style={{ color: '#000' }}>
                    {r.name}
                  </option>
                ))}
              </select>
              <div
                style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.1em', color: T.inkFaint,
                  marginTop: 8,
                }}
              >
                {region.states.toUpperCase()} &nbsp;·&nbsp; COST INDEX {Math.round(region.col * 100)}
              </div>
            </div>

            {/* Story tiles */}
            <div style={{ marginBottom: 40 }}>
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 16,
                }}
              >
                ◆ HEIGHT
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {STORIES.map((s) => (
                  <button
                    key={s.id}
                    className={`opt-tile ${storyId === s.id ? 'active' : ''}`}
                    onClick={() => setStoryId(s.id)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${T.rule}`,
                      borderLeft: `2px solid transparent`,
                      padding: '14px 18px',
                      cursor: 'pointer',
                      fontFamily: fontBody,
                      color: T.ink,
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 16,
                    }}
                  >
                    <div>
                      <div style={{ fontFamily: fontDisplay, fontSize: 16, marginBottom: 2 }}>
                        {s.label}
                      </div>
                      <div style={{ fontSize: 12, color: T.inkSoft }}>
                        {s.desc}
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: fontMono, fontSize: 11,
                        letterSpacing: '0.1em',
                        color: storyId === s.id ? T.sienna : T.inkFaint,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ×{s.mult.toFixed(1)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Accessibility tiles */}
            <div style={{ marginBottom: 40 }}>
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 16,
                }}
              >
                ◆ ACCESSIBILITY
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {ACCESS.map((a) => (
                  <button
                    key={a.id}
                    className={`opt-tile ${accessId === a.id ? 'active' : ''}`}
                    onClick={() => setAccessId(a.id)}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${T.rule}`,
                      borderLeft: `2px solid transparent`,
                      padding: '14px 18px',
                      cursor: 'pointer',
                      fontFamily: fontBody,
                      color: T.ink,
                      textAlign: 'left',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 16,
                    }}
                  >
                    <div>
                      <div style={{ fontFamily: fontDisplay, fontSize: 16, marginBottom: 2 }}>
                        {a.label}
                      </div>
                      <div style={{ fontSize: 12, color: T.inkSoft }}>
                        {a.desc}
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: fontMono, fontSize: 11,
                        letterSpacing: '0.1em',
                        color: accessId === a.id ? T.sienna : T.inkFaint,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ×{a.mult.toFixed(1)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Hourly value slider */}
            <div style={{ marginBottom: 40 }}>
              <div
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'baseline', marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.sienna,
                  }}
                >
                  ◆ YOUR TIME, PER HOUR
                </div>
                <div
                  style={{
                    fontFamily: fontDisplay, fontSize: 32, fontWeight: 400,
                    letterSpacing: '-0.02em', color: T.ink,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${hourlyValue}
                </div>
              </div>
              <input
                type="range"
                min="0"
                max="200"
                step="5"
                value={hourlyValue}
                onChange={(e) => setHourlyValue(parseInt(e.target.value))}
                style={{ width: '100%' }}
              />
              <div
                style={{
                  display: 'flex', justifyContent: 'space-between',
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.1em', color: T.inkFaint,
                  marginTop: 8,
                }}
              >
                <span>$0</span>
                <span>RETIRED ≈ $0</span>
                <span>AVG ≈ $35</span>
                <span>PRO ≈ $100</span>
                <span>$200</span>
              </div>
              <div
                style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.05em', color: T.inkFaint,
                  marginTop: 12, lineHeight: 1.5,
                }}
              >
                What an hour of your time is worth — your wage, your hourly billable, or zero if you would otherwise spend it relaxing. Honest answers produce honest math.
              </div>
            </div>
          </div>

          {/* RIGHT — output panel */}
          <div>
            {/* Pro estimate panel */}
            <div
              style={{
                background: T.surface,
                borderLeft: `2px solid ${T.sienna}`,
                padding: '32px 32px 28px',
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 12,
                }}
              >
                ◆ PROFESSIONAL
              </div>
              <div
                className="summary-num"
                style={{
                  fontFamily: fontDisplay, fontSize: 64, fontWeight: 300,
                  lineHeight: 0.95, letterSpacing: '-0.03em',
                  color: T.ink, fontVariantNumeric: 'tabular-nums',
                  marginBottom: 8,
                }}
              >
                ${proEstimate.mid}
              </div>
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.12em', color: T.inkFaint,
                }}
              >
                ${proEstimate.low}–${proEstimate.high} TYPICAL RANGE
              </div>
              {proEstimate.hitMin && (
                <div
                  style={{
                    marginTop: 10,
                    fontFamily: fontMono, fontSize: 10,
                    letterSpacing: '0.1em', color: T.sienna,
                  }}
                >
                  ◆ MOST PROS HAVE A ${PRO_MIN_CHARGE} JOB MINIMUM
                </div>
              )}
              <div
                style={{
                  marginTop: 16,
                  paddingTop: 14,
                  borderTop: `1px solid ${T.rule}`,
                  fontFamily: fontBody, fontSize: 13, color: T.inkSoft,
                  lineHeight: 1.5,
                }}
              >
                Includes labor, equipment, insurance, and travel. One-time clean.
                Add 10–15% if you want them to clean tracks, screens, and sills too.
              </div>
            </div>

            {/* DIY estimate panel */}
            <div
              style={{
                background: T.surface,
                borderLeft: `2px solid ${T.sage}`,
                padding: '32px 32px 28px',
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sage,
                  marginBottom: 12,
                }}
              >
                ◆ DIY (CASH ONLY)
              </div>
              <div
                className="summary-num"
                style={{
                  fontFamily: fontDisplay, fontSize: 64, fontWeight: 300,
                  lineHeight: 0.95, letterSpacing: '-0.03em',
                  color: T.ink, fontVariantNumeric: 'tabular-nums',
                  marginBottom: 8,
                }}
              >
                ${diyEstimate.totalCashOnly}
              </div>
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.12em', color: T.inkFaint,
                }}
              >
                + {diyEstimate.hours} HOURS OF YOUR TIME
              </div>
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 14,
                  borderTop: `1px solid ${T.rule}`,
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  rowGap: 6,
                  fontFamily: fontBody,
                  fontSize: 13,
                }}
              >
                <span style={{ color: T.inkSoft }}>Materials (amortized)</span>
                <span style={{ color: T.ink, fontVariantNumeric: 'tabular-nums' }}>${diyEstimate.materials}</span>
                <span style={{ color: T.inkSoft }}>Time at ${hourlyValue}/hr</span>
                <span style={{ color: T.ink, fontVariantNumeric: 'tabular-nums' }}>${diyEstimate.timeValue}</span>
                <span style={{ color: T.inkSoft, fontWeight: 500, paddingTop: 8, borderTop: `1px solid ${T.rule}` }}>True cost (incl. time)</span>
                <span style={{ color: T.ink, fontVariantNumeric: 'tabular-nums', fontWeight: 500, paddingTop: 8, borderTop: `1px solid ${T.rule}` }}>${diyEstimate.totalWithTime}</span>
              </div>
            </div>

            {/* Verdict */}
            <div
              style={{
                background: T.bg,
                border: `1px solid ${T.rule}`,
                padding: '24px 28px',
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 10,
                }}
              >
                ⚡ THE VERDICT
              </div>
              <div
                style={{
                  fontFamily: fontDisplay, fontSize: 22, fontWeight: 400,
                  lineHeight: 1.25, letterSpacing: '-0.01em',
                  color: T.ink, marginBottom: 8,
                }}
              >
                {savings > 0
                  ? <>DIY saves <em style={{ color: T.sienna }}>${Math.abs(savings)}</em> ({savingsPercent}%)</>
                  : <>Pro saves <em style={{ color: T.sienna }}>${Math.abs(savings)}</em> ({Math.abs(savingsPercent)}%)</>}
              </div>
              <div
                style={{
                  fontFamily: fontBody, fontSize: 13,
                  color: T.inkSoft, lineHeight: 1.5,
                }}
              >
                {story.risk === 'high' && (
                  <span style={{ color: T.sienna }}>
                    Three-story DIY is rope-access work. <strong>Hire the pro.</strong>{' '}
                  </span>
                )}
                {story.risk !== 'high' && hourlyValue > breakevenHourly && (
                  <>Above ${breakevenHourly}/hr, the pro becomes the better deal. Yours: ${hourlyValue}/hr.</>
                )}
                {story.risk !== 'high' && hourlyValue <= breakevenHourly && (
                  <>DIY makes sense below about ${breakevenHourly}/hr. Yours: ${hourlyValue}/hr.</>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Assumptions panel — collapsible */}
        <div className="reveal reveal-3" style={{ marginBottom: 56 }}>
          <button
            onClick={() => setShowAssumptions(!showAssumptions)}
            style={{
              background: 'transparent', border: 'none',
              color: T.inkSoft, fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', cursor: 'pointer',
              padding: '12px 0', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <span>{showAssumptions ? '−' : '+'}</span>
            <span>SHOW THE ASSUMPTIONS</span>
          </button>

          {showAssumptions && (
            <div
              style={{
                background: T.surface,
                border: `1px solid ${T.rule}`,
                padding: '28px 32px',
                marginTop: 12,
                fontFamily: fontBody, fontSize: 14,
                color: T.ink, lineHeight: 1.65,
              }}
            >
              <h3
                style={{
                  fontFamily: fontDisplay, fontSize: 22, fontWeight: 400,
                  margin: '0 0 16px',
                }}
              >
                What's under the hood
              </h3>
              <p style={{ marginBottom: 16, color: T.inkSoft }}>
                The numbers are deliberate, not magical. Here is exactly how they are computed.
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, marginBottom: 24 }}>
                <div>
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 11,
                      letterSpacing: '0.18em', color: T.sienna,
                      marginBottom: 12,
                    }}
                  >
                    PROFESSIONAL
                  </div>
                  <ul style={{ paddingLeft: 18, margin: 0, color: T.inkSoft }}>
                    <li>Base: ${PRO_BASE_PER_PANE.toFixed(2)} per pane (national average, IWCA 2024)</li>
                    <li>× regional cost-of-living index ({region.col.toFixed(2)} for your area)</li>
                    <li>× story multiplier ({story.mult.toFixed(1)})</li>
                    <li>× accessibility multiplier ({access.mult.toFixed(1)})</li>
                    <li>${PRO_MIN_CHARGE} minimum for any service call</li>
                    <li>±20% range reflects real-world quote variance</li>
                  </ul>
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 11,
                      letterSpacing: '0.18em', color: T.sage,
                      marginBottom: 12,
                    }}
                  >
                    DIY
                  </div>
                  <ul style={{ paddingLeft: 18, margin: 0, color: T.inkSoft }}>
                    <li>${DIY_KIT_COST} starter kit, amortized over {DIY_KIT_LIFETIME_JOBS} jobs</li>
                    <li>${DIY_PER_JOB_CONSUMABLES} per-job consumables (distilled water, soap)</li>
                    <li>{DIY_HOURS_PER_PANE * 60} minutes per pane at pro pace</li>
                    <li>× {DIY_ROOKIE_MULT} for first-timer pace</li>
                    <li>× story and accessibility multipliers</li>
                    <li>× your hourly time value (the honest one)</li>
                  </ul>
                </div>
              </div>

              <div
                style={{
                  paddingTop: 20,
                  borderTop: `1px solid ${T.rule}`,
                  color: T.inkSoft, fontSize: 13,
                }}
              >
                The DIY estimate assumes you already own a starter kit, or amortize one over its
                lifetime. First-job-ever costs more — about ${DIY_KIT_COST + DIY_PER_JOB_CONSUMABLES} cash plus your time. The kit lasts
                roughly two years for a typical homeowner cleaning twice annually.
              </div>
            </div>
          )}
        </div>

        {/* Cross-links */}
        <div
          className="reveal reveal-4"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 0,
            borderTop: `1px solid ${T.rule}`,
            marginBottom: 40,
          }}
        >
          {[
            ['THE TOOLS', 'The Solution Calculator →', '/tools/solution-calculator'],
            ['TECHNIQUE LIBRARY', 'Watch the four-stage wash before you DIY', '/tools/technique-library'],
            ['PRO TRACK', 'How working pros actually price residential', '/articles/pricing-first-commercial-route'],
          ].map(([kicker, title, to]) => (
            <Link
              key={title}
              to={to}
              className="ghost-btn"
              style={{
                padding: '20px 0',
                borderBottom: `1px solid ${T.rule}`,
                textDecoration: 'none',
                color: T.ink,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 10,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 8,
                }}
              >
                {kicker}
              </div>
              <div
                style={{
                  fontFamily: fontDisplay, fontSize: 17,
                  lineHeight: 1.3, paddingRight: 16,
                }}
              >
                {title}
              </div>
            </Link>
          ))}
        </div>

        {/* Footer */}
        <footer
          style={{
            marginTop: 40, paddingTop: 24,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', flexWrap: 'wrap', gap: 16,
            fontFamily: fontMono, fontSize: 11,
            color: T.inkFaint, letterSpacing: '0.1em',
          }}
        >
          <span>NO. 042 &nbsp;·&nbsp; TOOL 05 OF 09</span>
          <Link
            to="/tools/cost-estimator/guide"
            style={{
              color: T.sienna,
              textDecoration: 'none',
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: '0.1em',
            }}
          >
            READ THE METHODOLOGY →
          </Link>
        </footer>
      </div>
    </div>
  );
}
